import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale,
  BarElement, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AppLayout from '../components/AppLayout';
import authFetch from '../api/authFetch';
import './StockInsight.css';

ChartJS.register(
  CategoryScale, LinearScale,
  BarElement, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
  ChartDataLabels,
);

// ── Constants ────────────────────────────────────────────────────
const MONTH_NAMES = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const COL_LABELS = { nifty50: 'N50', fo: 'FO', cash: 'CASH' };
const COL_CLASSES = { nifty50: 'n50', fo: 'fo', cash: 'oth' };

// ── Helpers ───────────────────────────────────────────────────────
const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/market`;
  const h = window.location.hostname;
  return (h === 'localhost' || h === '127.0.0.1')
    ? '/api/market'
    : `http://${h}:5001/api/market`;
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const fmtDateLong = (iso) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// ── Component ─────────────────────────────────────────────────────
const StockInsight = () => {
  // Filter state
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState('nifty50');

  // Stock list state
  const [allSymbols, setAllSymbols] = useState([]);   // { symbol, totalCsValue }[] for selectedCollection
  const [symbolIndex, setSymbolIndex] = useState({}); // { SYMBOL: 'nifty50'|'fo'|'others' } — cross-collection index for search
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // Chart data
  const [marketTrendData, setMarketTrendData] = useState([]);  // aggregate for month
  const [stockTrendData, setStockTrendData] = useState([]);    // selected stock month data

  // Bar chart state (stock CS value per date)
  const [barData, setBarData] = useState(null);
  const [barLabel, setBarLabel] = useState('Select a stock');
  const [barLoading, setBarLoading] = useState(false);

  // Line chart state (market/stock trend)
  const [lineData, setLineData] = useState(null);
  const [lineLabel, setLineLabel] = useState('Select year & month');
  const [lineLoading, setLineLoading] = useState(false);

  // Value panel
  const [valuePanelData, setValuePanelData] = useState([]);

  // Toast
  const [toast, setToast] = useState({ show: false, msg: '', type: 'info' });
  const toastTimerRef = useRef(null);

  // Copy symbols state
  const [copiedSymbols, setCopiedSymbols] = useState(false);

  // Sort order: 'desc' | 'asc' | 'alpha'
  const [sortOrder, setSortOrder] = useState('desc');

  // Mobile/tablet filter bar visibility
  const [filterOpen, setFilterOpen] = useState(false);

  // Stock list DOM ref for scrolling
  const stockListRef = useRef(null);
  const selectedSymbolRef = useRef(selectedSymbol);
  selectedSymbolRef.current = selectedSymbol;

  const API_BASE = getApiBase();

  // ── Toast ──────────────────────────────────────────────────────
  const showToast = useCallback((msg, type = 'info') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, msg, type });
    toastTimerRef.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
  }, []);

  // ── Init: Load years ───────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const res = await authFetch(`${API_BASE}/dates`);
        const data = await res.json();
        const yrSet = new Set();
        (data.allDates || []).forEach(d => {
          const y = new Date(d).getFullYear();
          if (!isNaN(y)) yrSet.add(y);
        });
        const sorted = [...yrSet].sort((a, b) => b - a);
        setYears(sorted);

        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const yr = yrSet.has(currentYear) ? currentYear : (sorted[0] || null);
        setSelectedYear(yr);
        setSelectedMonth(currentMonth);
      } catch (err) {
        console.error('Init error:', err);
        showToast('Failed to load available dates', 'error');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load month data when year/month/collection changes ─────────
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;
    loadMonthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedCollection]);

  // ── Load cross-collection symbol index when year/month changes ─
  useEffect(() => {
    if (!selectedYear || !selectedMonth) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/symbols/index?year=${selectedYear}&month=${selectedMonth}`);
        const data = await res.json();
        if (!cancelled) setSymbolIndex(data.index || {});
      } catch (err) {
        console.error('symbol index error:', err);
        if (!cancelled) setSymbolIndex({});
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const loadMonthData = async () => {
    setLineLoading(true);
    setBarData(null);
    setBarLabel('Select a stock');
    setValuePanelData([]);

    try {
      const [symbolsRes, marketRes] = await Promise.all([
        authFetch(`${API_BASE}/symbols/${selectedCollection}?year=${selectedYear}&month=${selectedMonth}`),
        authFetch(`${API_BASE}/calendar/${selectedCollection}/aggregate?year=${selectedYear}&month=${selectedMonth}`),
      ]);

      const symbolsData = await symbolsRes.json();
      const marketData = await marketRes.json();

      const syms = (symbolsData.symbolsData || []).map(s => ({
        symbol: s.symbol,
        totalCsValue: typeof s.totalCsValue === 'object' && s.totalCsValue?.$numberDecimal
          ? parseFloat(s.totalCsValue.$numberDecimal)
          : parseFloat(s.totalCsValue || 0),
      }));
      setAllSymbols(syms);
      setSearchQuery('');
      setFilteredSymbols(syms);

      const trend = data => data.map(d => ({
        ...d,
        csValue: typeof d.csValue === 'object' && d.csValue?.$numberDecimal
          ? parseFloat(d.csValue.$numberDecimal)
          : parseFloat(d.csValue || 0),
      }));

      const mData = trend(marketData.dailyData || []);
      setMarketTrendData(mData);
      const mLabel = `${COL_LABELS[selectedCollection]} Market — ${MONTH_NAMES[selectedMonth]} ${selectedYear}`;
      setLineData(buildLineData(mData));
      setLineLabel(mLabel);

      // If a stock was previously selected, reload it
      if (selectedSymbolRef.current) {
        loadStockTrend(selectedSymbolRef.current);
      }
    } catch (err) {
      console.error('loadMonthData error:', err);
      showToast('Failed to load data', 'error');
      setLineData(null);
    } finally {
      setLineLoading(false);
    }
  };

  // ── Filter symbols on search ────────────────────────────────────
  // Empty query → current collection only (preserves cumulative view).
  // Non-empty query → union across all collections via symbolIndex, tag each row with its source.
  useEffect(() => {
    const q = searchQuery.trim().toUpperCase();
    let result;

    if (!q) {
      result = allSymbols.map(s => ({ ...s, collection: selectedCollection }));
    } else {
      const currentMap = new Map(allSymbols.map(s => [s.symbol, s.totalCsValue]));
      const seen = new Set();
      result = [];

      // Rows from current collection first (they carry CS values)
      for (const s of allSymbols) {
        if (!s.symbol.includes(q)) continue;
        seen.add(s.symbol);
        result.push({ ...s, collection: selectedCollection });
      }

      // Cross-collection matches from the index (no CS value available client-side)
      for (const sym of Object.keys(symbolIndex)) {
        if (seen.has(sym) || !sym.includes(q)) continue;
        const col = symbolIndex[sym];
        if (col === selectedCollection) continue;
        result.push({
          symbol: sym,
          collection: col,
          totalCsValue: currentMap.get(sym) ?? 0,
          crossCategory: true,
        });
      }
    }

    if (sortOrder === 'desc') result.sort((a, b) => b.totalCsValue - a.totalCsValue);
    else if (sortOrder === 'asc') result.sort((a, b) => a.totalCsValue - b.totalCsValue);
    else if (sortOrder === 'alpha') result.sort((a, b) => a.symbol.localeCompare(b.symbol));
    setFilteredSymbols(result);
  }, [searchQuery, allSymbols, sortOrder, symbolIndex, selectedCollection]);

  // ── Select stock ────────────────────────────────────────────────
  // targetCollection lets a cross-category search hit auto-switch collections.
  const selectStock = useCallback((symbol, targetCollection) => {
    const col = targetCollection || selectedCollection;

    if (col !== selectedCollection) {
      setSelectedSymbol(symbol);
      setSelectedCollection(col);
      return;
    }

    setSelectedSymbol(symbol);
    setTimeout(() => {
      const el = stockListRef.current?.querySelector(`[data-symbol="${symbol}"]`);
      if (el) {
        const list = stockListRef.current;
        const elTop = el.offsetTop;
        const elBot = elTop + el.offsetHeight;
        const visTop = list.scrollTop;
        const visBot = visTop + list.clientHeight;
        if (elTop < visTop) list.scrollTop = elTop - 4;
        else if (elBot > visBot) list.scrollTop = elBot - list.clientHeight + 4;
      }
    }, 0);
    loadStockTrend(symbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, selectedCollection]);

  const loadStockTrend = async (symbol) => {
    if (!selectedYear || !selectedMonth || !symbol) return;
    setBarLoading(true);
    setLineLoading(true);
    setBarLabel(symbol);
    setLineLabel(symbol);
    try {
      const res = await authFetch(
        `${API_BASE}/calendar/${selectedCollection}?symbol=${encodeURIComponent(symbol)}&year=${selectedYear}&month=${selectedMonth}`
      );
      const data = await res.json();

      const raw = (data.dailyData || []).map(d => ({
        ...d,
        csValue: typeof d.csValue === 'object' && d.csValue?.$numberDecimal
          ? parseFloat(d.csValue.$numberDecimal)
          : parseFloat(d.csValue || 0),
      }));

      setStockTrendData(raw);
      setBarData(buildBarData(raw));
      setLineData(buildLineData(raw));
      setValuePanelData(raw);
    } catch (err) {
      console.error('loadStockTrend error:', err);
      showToast('Failed to load stock data', 'error');
    } finally {
      setBarLoading(false);
      setLineLoading(false);
    }
  };

  // ── Keyboard navigation ─────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown')  { e.preventDefault(); navigateStock(+1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); navigateStock(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSymbols, selectedSymbol]);

  const navigateStock = (dir) => {
    if (!filteredSymbols.length) return;
    let idx = filteredSymbols.findIndex(s => s.symbol === selectedSymbolRef.current);
    if (idx === -1) idx = dir === 1 ? 0 : filteredSymbols.length - 1;
    else {
      idx += dir;
      if (idx < 0) idx = filteredSymbols.length - 1;
      if (idx >= filteredSymbols.length) idx = 0;
    }
    const next = filteredSymbols[idx];
    selectStock(next.symbol, next.collection);
  };

  const navPosText = () => {
    if (!selectedSymbol || !filteredSymbols.length) return '';
    const idx = filteredSymbols.findIndex(s => s.symbol === selectedSymbol);
    return idx !== -1 ? `${idx + 1}/${filteredSymbols.length}` : '';
  };

  // ── Chart builders ──────────────────────────────────────────────
  const buildBarData = (data) => {
    if (!data.length) return null;
    const labels = data.map(d => fmtDate(d.date));
    const values = data.map(d => d.csValue);
    return {
      labels,
      datasets: [{
        label: 'CS Value',
        data: values,
        backgroundColor: values.map(v => v >= 0 ? 'rgba(0,242,96,0.72)' : 'rgba(255,107,107,0.72)'),
        borderColor:     values.map(v => v >= 0 ? '#00f260' : '#ff6b6b'),
        borderWidth: 1,
        borderRadius: 3,
      }],
    };
  };

  const buildLineData = (data) => {
    if (!data.length) return null;
    const labels = data.map(d => fmtDate(d.date));
    const values = data.map(d => d.csValue);
    const pointColors = values.map(v => v >= 0 ? '#00f260' : '#ff6b6b');
    return {
      labels,
      datasets: [{
        label: 'CS Value',
        data: values,
        borderColor: '#00f260',
        borderWidth: 2,
        segment: {
          borderColor: ctx => {
            const y0 = ctx.p0.parsed.y;
            const y1 = ctx.p1.parsed.y;
            return (y0 >= 0 && y1 >= 0) ? '#00f260'
                 : (y0 <  0 && y1 <  0) ? '#ff6b6b'
                 : '#ffd200';
          },
        },
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.35,
        fill: true,
        backgroundColor: 'rgba(0,242,96,0.1)',
        spanGaps: true,
      }],
    };
  };

  // ── Chart helpers ────────────────────────────────────────────────
  const fmtK = (value) => {
    if (value === 0) return '';
    const abs = Math.abs(value);
    if (abs >= 1000) return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return Number(value).toFixed(1);
  };

  // ── Chart options ───────────────────────────────────────────────
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 350 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(12,12,26,0.95)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.15)',
        callbacks: {
          label: ctx => ` CS Value: ${Number(ctx.parsed.y).toFixed(2)}`,
        },
      },
      datalabels: {
        color: 'rgba(200,200,200,0.6)',
        font: { size: 9 },
        formatter: (value) => fmtK(value),
        anchor: (ctx) => ctx.raw >= 0 ? 'end' : 'start',
        align: (ctx) => ctx.raw >= 0 ? 'top' : 'bottom',
        offset: 2,
      },
    },
    scales: {
      x: {
        ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 9 }, maxRotation: 45 },
        grid: { color: 'rgba(255,255,255,0.04)' },
      },
      y: {
        ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 9 } },
        grid: { color: 'rgba(255,255,255,0.06)' },
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 450 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(12,12,26,0.95)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.15)',
        callbacks: {
          label: ctx => ` CS Value: ${Number(ctx.parsed.y).toFixed(2)}`,
        },
      },
      datalabels: {
        color: 'rgba(200,200,200,0.6)',
        font: { size: 9 },
        formatter: (value) => fmtK(value),
        anchor: (ctx) => ctx.raw >= 0 ? 'end' : 'start',
        align: (ctx) => ctx.raw >= 0 ? 'top' : 'bottom',
        offset: 2,
      },
    },
    scales: {
      x: {
        ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 9 }, maxRotation: 45 },
        grid: { color: 'rgba(255,255,255,0.04)' },
      },
      y: {
        ticks: { color: 'rgba(255,255,255,0.5)', font: { size: 9 } },
        grid: { color: 'rgba(255,255,255,0.06)' },
        border: { dash: [3, 3] },
      },
    },
  };

  // ── Value Panel renderer ────────────────────────────────────────
  const renderValuePanel = () => {
    if (!valuePanelData.length) {
      return <div className="sm-value-empty">Select a stock</div>;
    }
    const maxAbs = Math.max(...valuePanelData.map(d => Math.abs(d.csValue)), 1);
    return valuePanelData.map((d, i) => {
      const val = d.csValue;
      const pct = (Math.abs(val) / maxAbs) * 100;
      const cls = val > 0 ? 'pos' : val < 0 ? 'neg' : 'zero';
      return (
        <div className="sm-value-row" key={i}>
          <span className="sm-value-date">{fmtDateLong(d.date)}</span>
          <div className="sm-value-bar-wrap">
            <div className={`sm-value-bar ${val >= 0 ? 'pos' : 'neg'}`} style={{ width: `${pct.toFixed(1)}%` }} />
          </div>
          <span className={`sm-value-num ${cls}`}>{val === 0 ? '0' : val.toFixed(0)}</span>
        </div>
      );
    });
  };

  // ── CS Calendar URL builder ────────────────────────────────────
  const calendarUrl = (sym) => {
    const y = selectedYear || new Date().getFullYear();
    const m = selectedMonth || (new Date().getMonth() + 1);
    return `/cs-calendar?symbol=${encodeURIComponent(sym)}&year=${y}&month=${m}&collection=${selectedCollection}`;
  };

  // ── Copy positive symbols for TradingView ───────────────────
  const copyPositiveSymbols = () => {
    const positives = allSymbols.filter(s => s.totalCsValue > 0);
    if (!positives.length) return;
    const text = positives.map(s => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedSymbols(true);
    setTimeout(() => setCopiedSymbols(false), 2000);
  };

  // ── Collection change ───────────────────────────────────────────
  const changeCollection = (col) => {
    setSelectedCollection(col);
    setSelectedSymbol(null);
    setBarData(null);
    setBarLabel('Select a stock');
    setValuePanelData([]);
  };

  // ── Refresh ─────────────────────────────────────────────────────
  const handleRefresh = async () => {
    if (selectedYear && selectedMonth) {
      await loadMonthData();
      showToast('Data refreshed', 'success');
    } else {
      showToast('Please select year and month', 'info');
    }
  };

  const colClass = COL_CLASSES[selectedCollection];

  return (
    <AppLayout pageBadge="Stock Insight">
    <div className={`sm-container${filterOpen ? ' filter-open' : ''}`}>
      {/* MOBILE/TABLET FILTER TOGGLE */}
      {!filterOpen && (
        <button
          type="button"
          className="sm-filter-toggle"
          onClick={() => setFilterOpen(true)}
          aria-label="Show filters"
        >
          <span>☰</span>
          <span>Filters</span>
        </button>
      )}

      {/* ── Filter Bar ── */}
      <div className={`sm-filter-bar${filterOpen ? ' is-open' : ''}`}>
        {/* Year */}
        <div className="sm-filter-group sm-filter-year">
          <span className="sm-filter-label">Year</span>
          <select
            className="sm-filter-select"
            value={selectedYear || ''}
            onChange={e => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">─ Year ─</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Month */}
        <div className="sm-filter-group sm-filter-month">
          <span className="sm-filter-label">Month</span>
          <select
            className="sm-filter-select"
            value={selectedMonth || ''}
            onChange={e => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">─ Month ─</option>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
              <option key={m} value={m}>{MONTH_NAMES[m]}</option>
            ))}
          </select>
        </div>

        <div className="sm-filter-divider" />

        {/* Collection toggle */}
        <div className="sm-filter-group sm-filter-market">
          <span className="sm-filter-label">Market</span>
          <div className="sm-col-toggle">
            {['nifty50', 'fo', 'cash'].map(col => (
              <button
                key={col}
                className={`sm-col-btn ${COL_CLASSES[col]}${selectedCollection === col ? ' active' : ''}`}
                onClick={() => changeCollection(col)}
              >
                {COL_LABELS[col]}
              </button>
            ))}
          </div>
        </div>

        {/* Selected stock info */}
        <div className="sm-selected-info">
          Stock: <strong>{selectedSymbol || '—'}</strong>
        </div>

        {/* Refresh */}
        <button
          className="sm-refresh-btn sm-filter-refresh"
          onClick={handleRefresh}
          disabled={!selectedYear || !selectedMonth}
        >
          ↻ Refresh
        </button>

        <button
          type="button"
          className="sm-refresh-btn sm-filter-close"
          onClick={() => setFilterOpen(false)}
          aria-label="Close filters"
        >
          <span>✕</span>
          <span>Close Filter</span>
        </button>
      </div>

      {/* ── Main Content ── */}
      <div className="sm-main">
        {/* ── Left: Stock List ── */}
        <div className="sm-stock-panel">
          <div className="sm-stock-panel-header">
            <div className="sm-stock-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div className="sm-stock-panel-title">
                Stocks
                <span className="sm-stock-count">{filteredSymbols.length}</span>
              </div>
              <button
                className="sm-copy-btn"
                onClick={copyPositiveSymbols}
                disabled={!allSymbols.filter(s => s.totalCsValue > 0).length}
                title="Copy all positive symbols for TradingView watchlist"
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: '0.68rem', fontWeight: 600,
                  padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                  border: copiedSymbols ? '1px solid #10b981' : '1px solid rgba(16,185,129,0.4)',
                  background: copiedSymbols ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.1)',
                  color: copiedSymbols ? '#6ee7b7' : '#10b981',
                  transition: 'all 0.2s',
                  opacity: allSymbols.filter(s => s.totalCsValue > 0).length ? 1 : 0.4,
                }}
              >
                {copiedSymbols ? '✓ Copied!' : '⧉ Copy'}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
              {[{ key: 'desc', label: '↓ CS' }, { key: 'asc', label: '↑ CS' }, { key: 'alpha', label: 'A–Z' }].map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSortOrder(opt.key)}
                  style={{
                    fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                    borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
                    border: sortOrder === opt.key ? '1px solid rgba(139,92,246,0.8)' : '1px solid rgba(255,255,255,0.12)',
                    background: sortOrder === opt.key ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
                    color: sortOrder === opt.key ? '#c4b5fd' : 'rgba(255,255,255,0.45)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              className="sm-stock-search"
              placeholder="Filter by symbol…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="sm-stock-list" ref={stockListRef}>
            {!filteredSymbols.length ? (
              <div className="sm-stock-empty">
                {allSymbols.length ? 'No matches' : 'Select year & month'}
              </div>
            ) : filteredSymbols.map(s => {
              const val = s.totalCsValue;
              const cls = val > 0 ? 'pos' : val < 0 ? 'neg' : 'zero';
              return (
                <div
                  key={`${s.collection}:${s.symbol}`}
                  data-symbol={s.symbol}
                  className={`sm-stock-item${selectedSymbol === s.symbol && s.collection === selectedCollection ? ' selected' : ''}`}
                  onClick={() => selectStock(s.symbol, s.collection)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {s.symbol}
                    {s.crossCategory && (
                      <span
                        className={`sm-col-btn ${COL_CLASSES[s.collection]} active`}
                        style={{ fontSize: '0.55rem', padding: '1px 5px', pointerEvents: 'none' }}
                        title={`In ${COL_LABELS[s.collection]} — click to switch`}
                      >
                        {COL_LABELS[s.collection]}
                      </span>
                    )}
                    <a
                      href={`https://in.tradingview.com/chart/?symbol=NSE%3A${s.symbol}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`View ${s.symbol} on TradingView`}
                      style={{ color: '#60a5fa', lineHeight: 1, flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                      </svg>
                    </a>
                    <a
                      href={calendarUrl(s.symbol)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`View ${s.symbol} CS Calendar`}
                      style={{ color: '#a78bfa', lineHeight: 1, flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </a>
                  </span>
                  <span className={`sm-stock-csv ${s.crossCategory ? 'zero' : cls}`}>
                    {s.crossCategory ? '—' : (val === 0 ? '0' : val.toFixed(0))}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="sm-nav-bar">
            <button className="sm-nav-key" onClick={() => navigateStock(-1)} title="Previous (↑)">▲</button>
            <button className="sm-nav-key" onClick={() => navigateStock(+1)} title="Next (↓)">▼</button>
            <span className="sm-nav-label">navigate</span>
            <span className="sm-nav-pos">{navPosText()}</span>
          </div>
        </div>

        {/* ── Centre: Charts ── */}
        <div className="sm-chart-panel">
          {/* Bar chart */}
          <div className="sm-chart-card bar">
            <div className="sm-chart-header">
              <div className="sm-chart-title">
                CS Value — <span>{barLabel}</span>
              </div>
              <div className="sm-chart-meta">
                <div className="sm-legend">
                  <div className="sm-legend-item">
                    <div className="sm-legend-dot" style={{ background: '#00f260' }} />
                    Positive
                  </div>
                  <div className="sm-legend-item">
                    <div className="sm-legend-dot" style={{ background: '#ff6b6b' }} />
                    Negative
                  </div>
                </div>
                <span className={`sm-chart-tag ${colClass}`}>{COL_LABELS[selectedCollection]}</span>
              </div>
            </div>
            <div className="sm-chart-body">
              {barLoading && (
                <div className="sm-chart-loading">
                  <div className="sm-spinner" /> Loading…
                </div>
              )}
              {!barLoading && !barData && (
                <div className="sm-chart-placeholder">
                  <div className="ph-icon">📊</div>
                  <div>Select a stock from the list</div>
                </div>
              )}
              {barData && <Bar data={barData} options={barOptions} />}
            </div>
          </div>

          {/* Line chart */}
          <div className="sm-chart-card line">
            <div className="sm-chart-header">
              <div className="sm-chart-title">
                Stock CS Value — <span>{lineLabel}</span>
              </div>
              <div className="sm-chart-meta">
                <span className={`sm-chart-tag ${colClass}`}>{COL_LABELS[selectedCollection]}</span>
              </div>
            </div>
            <div className="sm-chart-body">
              {lineLoading && (
                <div className="sm-chart-loading">
                  <div className="sm-spinner" /> Loading…
                </div>
              )}
              {!lineLoading && !lineData && (
                <div className="sm-chart-placeholder">
                  <div className="ph-icon">📉</div>
                  <div>Select year &amp; month to load chart</div>
                </div>
              )}
              {lineData && <Line data={lineData} options={lineOptions} />}
            </div>
          </div>
        </div>

        {/* ── Right: Value Panel ── */}
        <div className="sm-value-panel">
          <div className="sm-value-panel-header">
            <div className="sm-value-panel-title">Stock CS Value</div>
            <div className="sm-value-panel-symbol">{selectedSymbol || '—'}</div>
          </div>
          <div className="sm-value-list">
            {renderValuePanel()}
          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      <div className={`sm-toast ${toast.show ? 'show' : ''} ${toast.type}`}>
        {toast.msg}
      </div>
    </div>
    </AppLayout>
  );
};

export default StockInsight;
